import { resolveChatBotSettings } from "@jugl-web/domain-resources/chat-bot/utils/resolveChatBotSettings";
import { DataLoadingWrapper, MultiSectionLayout } from "@jugl-web/ui-kit";
import { getUniqueId } from "@jugl-web/utils";
import { useManageAccountsDrawer } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/providers/ManageAccountsDrawerContext";
import { ChatBotChannelPreview } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/screens/WebChatBotAccountScreen/components/ChatBotChannelPreview";
import { ChatbotPreviewBackground } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/screens/WebChatBotAccountScreen/components/ChatbotPreviewBackground";
import { FC, useState } from "react";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { SetupAndTestTabMenu } from "./components/SetupAndTestTabMenu";
import { ReviseAnswerSidebar } from "./components/ReviseAnswerSidebar";

export const SetupAndTestTabContent: FC<{
  entityId: string;
  onAddAutomation: () => void;
}> = ({ entityId, onAddAutomation }) => {
  const { integrationsApi } = useRestApiProvider();
  const { isAnonymous } = useAnonymousSession();
  const [revisePayload, setRevisePayload] = useState<{
    question: string;
    agentAnswer: string;
  }>();
  const { crmConfig, isCRMConfigLoading, isCRMConfigError, refetchCRMConfig } =
    useManageAccountsDrawer();
  const chatBotSettings = resolveChatBotSettings(crmConfig?.chatbot?.settings);

  const {
    isLoading: isConnectionsLoading,
    isError: isConnectionsError,
    refetch: refetchConnections,
  } = integrationsApi.useGetConnectionsQuery({
    entityId,
  });
  const {
    isLoading: isAgentConfigLoading,
    isError: isAgentConfigError,
    refetch: refetchAgentConfig,
  } = integrationsApi.useGetAiAgentConfigQuery({
    entityId,
    meta: { silent: true },
  });

  return (
    <DataLoadingWrapper
      isLoading={
        isCRMConfigLoading || isConnectionsLoading || isAgentConfigLoading
      }
      isError={isCRMConfigError || isConnectionsError || isAgentConfigError}
      onRetry={() => {
        if (isCRMConfigError) {
          refetchCRMConfig();
        }
        if (isConnectionsError) {
          refetchConnections();
        }
        if (isAgentConfigError) {
          refetchAgentConfig();
        }
      }}
    >
      <MultiSectionLayout
        leftSide={
          <SetupAndTestTabMenu
            entityId={entityId}
            onAddAutomation={onAddAutomation}
          />
        }
        className="animate-fade-in"
        classNames={{
          content: "flex items-center justify-center",
        }}
      >
        <ChatbotPreviewBackground>
          <ChatBotChannelPreview
            entityId={entityId}
            chatName={chatBotSettings.chatName}
            agentName={chatBotSettings.agentName}
            welcomeMessage={chatBotSettings.welcomeMessage}
            senderMessageBackgroundColor={
              chatBotSettings.senderMessageBackgroundColor
            }
            theme={chatBotSettings.theme}
            buttonColor={chatBotSettings.buttonColor}
            alignment={chatBotSettings.alignment}
            bottomSpacing={chatBotSettings.bottomSpacing}
            sideSpacing={chatBotSettings.sideSpacing}
            chatImage={chatBotSettings.chatImageUrl}
            agentImage={chatBotSettings.agentImage}
            buttonIcon={chatBotSettings.buttonIcon}
            ctaVisual={chatBotSettings.ctaVisual?.url ?? null}
            ctas={chatBotSettings.ctas.map((item) => ({
              id: getUniqueId(),
              value: item,
            }))}
            suggestions={chatBotSettings.suggestions.map((item) => ({
              id: getUniqueId(),
              value: item,
            }))}
            // For now, always disable Voice Mode in the "Setup & Test" tab
            voice_config={{
              ...chatBotSettings.voice_config,
              enabled: false,
            }}
            chatMode="active"
            previewMode="chat-only"
            onRevise={!isAnonymous ? setRevisePayload : undefined}
          />
        </ChatbotPreviewBackground>
      </MultiSectionLayout>
      <ReviseAnswerSidebar
        entityId={entityId}
        isOpen={!!revisePayload}
        onRequestClose={() => setRevisePayload(undefined)}
        question={revisePayload?.question || ""}
        agentAnswer={revisePayload?.agentAnswer || ""}
      />
    </DataLoadingWrapper>
  );
};
